<template>
    <form-wrapper class="account-information-form" @submit="handleFormSubmission(data)">
        <form-row>
            <form-cell width="half">
                <text-field
                    :label="$t('forms.profile.account_information.fields.first_name.label')"
                    :placeholder="$t('forms.profile.account_information.fields.first_name.placeholder')"
                    :isRequired="true"
                    :formData="data"
                    valuePath="firstName"
                    @change="change($event)"
                ></text-field>
            </form-cell>
            <form-cell width="half">
                <text-field
                    :label="$t('forms.auth.register.fields.middle_name.label')"
                    :placeholder="$t('forms.auth.register.fields.middle_name.placeholder')"
                    :isRequired="false"
                    :formData="data"
                    valuePath="middleName"
                    @change="change($event)"
                ></text-field>
            </form-cell>
            <form-cell width="half">
                <text-field
                    :label="$t('forms.profile.account_information.fields.last_name.label')"
                    :placeholder="$t('forms.profile.account_information.fields.last_name.placeholder')"
                    :isRequired="true"
                    :formData="data"
                    valuePath="lastName"
                    @change="change($event)"
                ></text-field>
            </form-cell>
        </form-row>

        <form-row v-if="!$auth.isPracticeAncillary()">
            <form-cell width="quarter">
                <select-field
                    :label="$t('forms.profile.account_information.fields.title.label')"
                    :placeholder="$t('forms.profile.account_information.fields.title.placeholder')"
                    :options="designationOptions"
                    :isRequired="true"
                    :formData="data"
                    valuePath="designation"
                    @change="change($event)"
                ></select-field>
            </form-cell>
            <form-cell width="quarter">
                <select-field
                    :label="$t('forms.profile.account_information.fields.gender.label')"
                    :placeholder="$t('forms.profile.account_information.fields.gender.placeholder')"
                    :options="genderOptions"
                    :formData="data"
                    valuePath="gender"
                    @change="change($event)"
                ></select-field>
            </form-cell>
            <form-cell width="half">
                <multi-select-field
                    :label="$t('forms.profile.account_information.fields.languages.label')"
                    :placeholder="$t('forms.profile.account_information.fields.languages.placeholder')"
                    :formData="data"
                    :options="languagesOptions"
                    valuePath="languages"
                    @change="change($event)"
                ></multi-select-field>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell width="third">
                <phone-field
                    :label="$t('forms.profile.account_information.fields.phone.label')"
                    :placeholder="$t('forms.profile.account_information.fields.phone.placeholder')"
                    :isRequired="true"
                    :formData="data"
                    valuePath="phone"
                    @change="change($event)"
                ></phone-field>
            </form-cell>
            <form-cell width="third">
                <common-content-spacer height="small">
                    <phone-field
                        :label="$t('forms.profile.account_information.fields.mobile_phone.label')"
                        :placeholder="$t('forms.profile.account_information.fields.mobile_phone.placeholder')"
                        :isRequired="false"
                        :formData="data"
                        valuePath="mobilePhone"
                        @change="change($event)"
                    ></phone-field>
                    <checkbox-field :formData="data" valuePath="isAgreedToSms" @change="change($event)">
                        <template #label>
                            <span>
                                {{ $t('forms.profile.account_information.fields.agree_to_sms.label') }}
                            </span>
                        </template>
                    </checkbox-field>
                </common-content-spacer>
            </form-cell>

            <form-cell v-if="isPhysicianSignedIn" width="third">
                <text-field
                    :label="$t('forms.profile.account_information.fields.email.label')"
                    :isDisabled="true"
                    :formData="data"
                    valuePath="email"
                    @change="change($event)"
                ></text-field>
                <common-button
                    :isSubmittable="false"
                    :label="$t('forms.profile.account_information.buttons.change_email')"
                    type="link"
                    @click="openChangeEmailModal"
                ></common-button>
            </form-cell>
        </form-row>

        <form-row v-if="isPhysicianSignedIn" class="account-information-form__password-row">
            <form-cell width="half">
                <form-field :label="$t('forms.profile.account_information.fields.password.label')">**********</form-field>
                <common-button
                    class="account-information-form__password-button"
                    :isSubmittable="false"
                    :label="$t('forms.profile.account_information.buttons.modify_password')"
                    type="link"
                    @click="openChangePasswordModal"
                ></common-button>
            </form-cell>
        </form-row>

        <template #footer>
            <common-buttons>
                <common-button
                    :isSubmittable="false"
                    :label="$t('pages.profile.professional.buttons.secondary')"
                    type="secondary"
                    size="large"
                    @click="goToIndex"
                ></common-button>
                <common-button
                    :isProcessing="isProcessing"
                    :label="$t('pages.profile.professional.buttons.primary')"
                    size="large"
                    type="primary"
                ></common-button>
            </common-buttons>
        </template>

        <change-password-modal
            :isOpen="isChangePasswordModalOpen"
            @close="closeChangePasswordModal"
            @password-changed="passwordChanged"
        ></change-password-modal>
        <change-email-modal
            :isOpen="isChangeEmailModalOpen"
            @close="closeChangeEmailModal"
            @email-changed="closeChangeEmailModal"
        ></change-email-modal>
        <common-notification ref="commonSuccessNotificationChild" :content="notificationMessage"></common-notification>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import HandleRequestMixin from 'mixins/HandleRequestMixin'
    import UserDesignationEnum from 'enums/UserDesignationEnum'
    import GenderEnum from 'enums/GenderEnum'
    import LanguagesEnum from 'enums/LanguagesEnum'
    import ChangePasswordModal from 'components/page-elements/profile/ChangePasswordModal'
    import ChangeEmailModal from 'components/page-elements/profile/ChangeEmailModal'
    import UsersAPI from 'api/UsersAPI'

    export default {
        mixins: [FormMixin, HandleRequestMixin],
        components: { ChangePasswordModal, ChangeEmailModal },

        data() {
            return {
                isChangePasswordModalOpen: false,
                isChangeEmailModalOpen: false,
                isProcessing: false,
                designationOptions: this.loadDesignationOptions(),
                notificationMessage: '',
                genderOptions: Object.values(GenderEnum).map((gender) => ({ label: gender, value: gender.toLowerCase() })),
                languagesOptions: Object.values(LanguagesEnum).map((name) => ({
                    heading: null,
                    label: name,
                    value: name,
                    options: [{ label: name, value: name.toLowerCase(), data: { id: name.toLowerCase(), name: name } }],
                })),
            }
        },

        computed: {
            isPhysicianSignedIn() {
                return this.$auth.getActiveMember().id === this.$auth.getUser().id
            },
        },

        methods: {
            handleFormSubmission(data) {
                this.isProcessing = true
                this.handleRequest(UsersAPI.update(this.$auth.getActiveMember().id, data))
                    .then(() => {
                        this.isProcessing = false
                        this.$emit('account-information-saved')
                    })
                    .catch(() => (this.isProcessing = false))
            },

            goToIndex() {
                this.$router.push({ name: 'index.home' })
            },

            loadDesignationOptions() {
                return Object.values(UserDesignationEnum).map((designation) => ({ value: designation, label: designation }))
            },

            openChangePasswordModal() {
                this.isChangePasswordModalOpen = true
            },

            closeChangePasswordModal() {
                this.isChangePasswordModalOpen = false
            },

            openChangeEmailModal() {
                this.isChangeEmailModalOpen = true
            },

            closeChangeEmailModal() {
                this.isChangeEmailModalOpen = false
            },

            passwordChanged() {
                this.closeChangePasswordModal()
                this.notificationMessage = this.$t('forms.profile.account_information.messages.password')
                this.$refs.commonSuccessNotificationChild.showNotification()
            },

            prefillFormData() {
                const {
                    languages,
                    gender,
                    firstName,
                    middleName,
                    lastName,
                    designation,
                    phone,
                    mobilePhone,
                    isAgreedToSms,
                    email,
                } = this.$auth.getActiveMember()
                this.prefillData({
                    languages,
                    gender,
                    firstName,
                    middleName,
                    lastName,
                    designation,
                    phone,
                    mobilePhone,
                    isAgreedToSms,
                    email,
                })
            },
        },

        mounted() {
            this.prefillFormData()
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';
    .account-information-form {
        &__password-row {
            margin-top: 0 !important;
            position: relative;
        }
    }
</style>
